import bitmapFontXml from "../generated/fonts/pixel.fnt" with { type: "text" };
import smallFontXml from "../generated/fonts/pixel-small.fnt" with { type: "text" };

/**
 * How wide a text is in a pixel face (the pixel face or the small pixel face), in logical pixels, for a size that holds
 * pixel-face copy (a HUD slot sized to the words it shows, a key glyph sized to its key's name, a label's room). The advances are the bitmap
 * font's, which `bun run ui:build` writes from the same measured glyph grid as the web font, advances
 * unchanged and with no kerning (ADR-061, "pixel fonts come from a measured glyph grid"), so a word set in
 * the DOM is this wide at 1x and a whole number of logical pixels. A measured face that changes moves every
 * size built on this. The small face (ADR-061's note of 2026-10-16) is measured the same way from its own bitmap font, so
 * the canvas and the DOM share one width function for both faces.
 */

/**
 * The pixel faces `pixelTextWidth` measures: the pixel face at its native 11 and the small face at its native 9.
 */
export type PixelFace = "pixel" | "small";

/**
 * Each glyph's advance in a BMFont description in its XML form.
 *
 * @param xml The description.
 * @returns The advance of each `char` element, by code point, in the order they appear.
 * @example
 * advancesFrom('<char id="32" xadvance="2"/>');
 * // Map { 32 => 2 }
 */
export function advancesFrom(xml: string): ReadonlyMap<number, number> {
  const advances = new Map<number, number>();
  for (const match of xml.matchAll(/<char\b[^>]*?\bid="(\d+)"[^>]*?\bxadvance="(-?\d+)"/g)) {
    advances.set(Number(match[1]), Number(match[2]));
  }
  return advances;
}

/**
 * Each face's advances, from the bitmap fonts bundled with the client.
 */
const ADVANCES: Readonly<Record<PixelFace, ReadonlyMap<number, number>>> = { pixel: advancesFrom(bitmapFontXml), small: advancesFrom(smallFontXml) };

/**
 * A text's width in a pixel face at 1x: the sum of its glyphs' advances.
 *
 * @param text The text, in the face's glyphs: for the pixel face the Latin and Greek glyphs Pixelify Sans ships and
 *   four Greek letters it lacks (ADR-061 amendment of 2026-09-25), for the small face printable ASCII.
 * @param face The face; the pixel face when omitted.
 * @returns Its width in logical pixels.
 * @throws RangeError when the text holds a character the face has no glyph for, which the DOM would set in
 *   a fallback face of another width.
 * @example
 * pixelTextWidth("Esc");
 * // 18
 */
export function pixelTextWidth(text: string, face: PixelFace = "pixel"): number {
  let width = 0;
  for (const character of text) {
    const code = character.codePointAt(0) as number;
    const advance = ADVANCES[face].get(code);
    if (advance === undefined) {
      throw new RangeError(`the ${face} face has no glyph for U+${code.toString(16).toUpperCase().padStart(4, "0")}`);
    }
    width += advance;
  }
  return width;
}
